import React, { useState, useEffect } from "react";
import { GLOSSARY_CATEGORIES } from "./api";
import { CustomIcon } from "../../shared/CustomIcon";

// ============================================================
// GlossaryEntryModal — add / edit a single bilingual entry.
// Leader-only. 300-char notes limit matches the backend validation.
// ============================================================

const NOTES_MAX = 300;

export default function GlossaryEntryModal({ open, entry, onSave, onDelete, onClose }) {
  const [french, setFrench] = useState("");
  const [english, setEnglish] = useState("");
  const [category, setCategory] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setFrench(entry?.french || "");
      setEnglish(entry?.english || "");
      setCategory(entry?.category || "");
      setNotes(entry?.notes || "");
      setError("");
      setSaving(false);
    }
  }, [open, entry]);

  if (!open) return null;

  const canSave = french.trim() && english.trim() && !saving;
  const remaining = NOTES_MAX - notes.length;
  const notesRed = remaining < 50;

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError("");
    try {
      await onSave({
        french: french.trim(),
        english: english.trim(),
        category: category || null,
        notes: notes.trim() || null,
      });
    } catch (err) {
      setError(err?.message || "Save failed");
      setSaving(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="translation-glossary-modal"
    >
      <form
        className="modal"
        onSubmit={submit}
        style={{ maxWidth: 520, width: "min(96vw, 520px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <CustomIcon name="document" size={16} />
            {entry ? "Edit Glossary Entry" : "Add Glossary Entry"}
          </h3>
          <button type="button" className="modal-close" onClick={onClose} data-testid="translation-glossary-modal-close">×</button>
        </div>
        <div className="modal-body" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 200px", minWidth: 180 }}>
              <label className="form-label" style={{ fontSize: 11 }}>French (required)</label>
              <input
                type="text"
                className="form-input"
                value={french}
                onChange={(e) => setFrench(e.target.value)}
                data-testid="translation-glossary-modal-french"
                autoFocus
                lang="fr"
                required
              />
            </div>
            <div style={{ flex: "1 1 200px", minWidth: 180 }}>
              <label className="form-label" style={{ fontSize: 11 }}>English (required)</label>
              <input
                type="text"
                className="form-input"
                value={english}
                onChange={(e) => setEnglish(e.target.value)}
                data-testid="translation-glossary-modal-english"
                lang="en"
                required
              />
            </div>
          </div>
          <div>
            <label className="form-label" style={{ fontSize: 11 }}>Category</label>
            <select
              className="form-input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="translation-glossary-modal-category"
            >
              <option value="">— None —</option>
              {GLOSSARY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label" style={{ fontSize: 11 }}>Notes (optional, max 300 chars)</label>
            <textarea
              className="form-input"
              rows={3}
              maxLength={NOTES_MAX}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="translation-glossary-modal-notes"
              style={{ resize: "vertical", fontFamily: "'Inter',sans-serif" }}
            />
            <div style={{
              textAlign: "right", fontSize: 11, marginTop: 4,
              color: notesRed ? "#f87171" : "var(--kc-sub)", fontWeight: 600,
            }}
            data-testid="translation-glossary-modal-notes-count"
            >{remaining} remaining</div>
          </div>
          {error && <div data-testid="translation-glossary-modal-error" style={{ color: "#f87171", fontSize: 12 }}>{error}</div>}
        </div>
        <div style={{
          padding: 14,
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
          borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)",
        }}>
          {entry && onDelete ? (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => {
                if (!window.confirm("Delete this glossary entry?")) return;
                onDelete(entry);
              }}
              data-testid="translation-glossary-modal-delete"
              style={{ color: "#f87171", borderColor: "#f87171" }}
            >Delete</button>
          ) : <span />}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn btn-outline" onClick={onClose} data-testid="translation-glossary-modal-cancel">Cancel</button>
            <button
              type="submit"
              className="btn"
              disabled={!canSave}
              data-testid="translation-glossary-modal-save"
              style={{
                background: canSave ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 35%, transparent)",
                color: "#111", fontWeight: 800,
                padding: "0 22px", minHeight: 42, borderRadius: 10,
                cursor: canSave ? "pointer" : "not-allowed",
              }}
            >{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </form>
    </div>
  );
}
